import { useState } from 'react';
import Button from '@/components/Button';
import NumberInput from '@/components/NumberInput';
import StoreSelectBox from '@/components/StoreSelectBox';
import { Organization } from '@/models/Organization';
import { Store } from '@/models/Store';
import { Supplier } from '@/models/Supplier';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface SupplierToStore {
  store?: Store | null;
  supplier: Supplier;
  customerCode: string;
  moq: number;
}

interface SupplierToStoreFormProps {
  supplier: Supplier;
  store?: Store;
  organization?: Organization;
  onSubmit: (value: SupplierToStore) => Promise<void>;
}

const SupplierToStoreForm = ({
  supplier,
  store,
  organization,
  onSubmit,
}: SupplierToStoreFormProps) => {
  const [loading, setLoading] = useState(false);

  const methods = useForm<SupplierToStore>({
    defaultValues: {
      supplier,
      store: store || null,
      customerCode: store?.customerCode || '',
      moq: store?.moq || 0,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const onSubmitForm = async (data: SupplierToStore) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="grid grid-cols-2 gap-4 lg:gap-8">
          <div className="col-span-2">
            <label className="block font-medium text-gray-700 mb-1">Supplier:</label>
            <div className="w-full border shadow rounded-md py-2 pl-3 cursor-default">
              {supplier.name}
            </div>
          </div>

          {organization && (
            <div className="col-span-2">
              <label className="block font-medium text-gray-700 mb-1">Organization:</label>
              <div className="w-full border shadow rounded-md py-2 pl-3 cursor-default">
                {organization.name}
              </div>
            </div>
          )}

          {store && (
            <div className="col-span-2">
              <label className="block font-medium text-gray-700 mb-1">Store:</label>
              <div className="w-full border shadow rounded-md py-2 pl-3 cursor-default">
                {store.name}
              </div>
            </div>
          )}

          {!store && (
            <div className="col-span-2">
              <Controller
                name="store"
                control={control}
                rules={{ required: 'Store is required' }}
                render={({ field }) => (
                  <StoreSelectBox
                    value={field.value || null}
                    onSelect={(selected) => field.onChange(selected || null)}
                    showOrganization
                  />
                )}
              />
              {errors.store && <span className="text-red-500 text-sm">{errors.store.message}</span>}
            </div>
          )}

          <div className="col-span-2">
            <label htmlFor="customerCode" className="block font-medium text-gray-700">
              Retailer Code:
            </label>
            <Controller
              name="customerCode"
              control={control}
              rules={{ required: 'Retailer code is required' }}
              render={({ field }) => (
                <input
                  type="text"
                  id="customerCode"
                  {...field}
                  className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                />
              )}
            />
            {errors.customerCode && (
              <p className="text-red-500 text-sm">{errors.customerCode.message}</p>
            )}
          </div>

          {/* MOQ Field */}
          <div className="col-span-2">
            <label htmlFor="moq" className="block font-medium text-gray-700 mb-1">
              Minimum Order Quantity (MOQ):
            </label>
            <Controller
              name="moq"
              control={control}
              rules={{
                required: 'MOQ is required',
                min: { value: 0, message: 'MOQ cannot be negative' },
              }}
              render={({ field }) => (
                <NumberInput
                  id="moq"
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  className="w-44"
                />
              )}
            />
            {errors.moq && <p className="text-red-500 text-sm">{errors.moq.message}</p>}
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            type="submit"
            icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
            loadingIconClassName="h-5 w-5 mr-1"
            variant="primary"
            loading={loading}
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default SupplierToStoreForm;
